import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Container, Ruler, Package, CheckCircle, AlertCircle } from 'lucide-react';
import { services } from '../mockData';
import { FORFAIT_INCLUS, SUPPLEMENTS, A_PREVOIR, TARIFS_NOTE, prefillCommande } from '../data/offre';
import { useAuth } from '../contexts/AuthContext';

const Services = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  return (
    <section id="tarifs" className="py-10 bg-gradient-to-b from-gray-50 to-white scroll-mt-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-green-100 text-green-700 hover:bg-green-100">
            Tarifs
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Location de <span className="text-green-600">bennes à déchets</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            De 3m³ à 30m³, choisissez la benne adaptée à votre chantier en Ile-de-France.
            Forfait tout compris : location 15 jours, transport et traitement.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => (
            <Card
              key={service.id}
              className="p-6 hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-green-200 group"
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">
                    {service.name}
                  </h3>
                  <Badge className="bg-green-100 text-green-700 text-xs">
                    Livraison {service.delay}
                  </Badge>
                </div>
                <div className="bg-green-50 p-3 rounded-lg group-hover:bg-green-100 transition-colors">
                  <Container className="h-6 w-6 text-green-600" />
                </div>
              </div>

              <p className="text-gray-600 mb-6">{service.description}</p>

              <div className="space-y-4 mb-6">
                <div className="flex items-center text-sm text-gray-600">
                  <Ruler className="h-4 w-4 mr-2 text-green-600" />
                  <span>{service.dimensions}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Package className="h-4 w-4 mr-2 text-green-600" />
                  <span>Capacité : {service.capacity}</span>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4 mb-6">
                <div className="space-y-2">
                  {service.priceGravats && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Gravats / Terre :</span>
                      <span className="font-bold text-green-600 text-lg">{service.priceGravats}</span>
                    </div>
                  )}
                  {service.priceMelange && <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Mélange (DIB) :</span>
                    <span className="font-bold text-green-600 text-lg">{service.priceMelange}</span>
                  </div>}
                </div>
              </div>

              <Button className="w-full bg-green-600 hover:bg-green-700 text-white group-hover:scale-105 transition-transform" onClick={() => { prefillCommande(service.capacity, service.priceGravats ? 'Gravat/terre' : 'Mélange (DIB)'); navigate(user ? '/mon-compte' : '/commander-sans-compte', { state: { from: 'pricing', volume: service.capacity } }); }}>
                Commander maintenant
              </Button>
            </Card>
          ))}
        </div>

        <div className="mt-12">
          <div id="conditions" className="scroll-mt-28 rounded-2xl border-2 border-gray-100 bg-white shadow-sm p-6 sm:p-8" data-testid="conditions-block">
            <h3 className="text-base md:text-lg font-bold text-gray-900 mb-1">Ce que comprend le forfait</h3>
            <p className="text-sm text-gray-600 mb-6" data-testid="conditions-tarifs-note">{TARIFS_NOTE}</p>
            <div className="grid md:grid-cols-3 gap-6 text-left">
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-green-700 mb-3">Inclus</p>
                <ul className="space-y-2" data-testid="conditions-inclus">
                  {FORFAIT_INCLUS.map((t) => (
                    <li key={t} className="flex items-start gap-2 text-sm text-gray-700"><CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />{t}</li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-gray-900 mb-3">Suppléments définis</p>
                <ul className="space-y-2" data-testid="conditions-supplements">
                  {SUPPLEMENTS.map((s) => (
                    <li key={s.label} className="flex items-start justify-between gap-3 text-sm text-gray-700"><span className="min-w-0">{s.label}</span><span className="font-semibold text-gray-900 text-right shrink-0 max-w-[45%]">{s.valeur}</span></li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-gray-900 mb-3">À prévoir sur place</p>
                <ul className="space-y-2" data-testid="conditions-a-prevoir">
                  {A_PREVOIR.map((t) => (
                    <li key={t} className="flex items-start gap-2 text-sm text-gray-700"><AlertCircle className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />{t}</li>
                  ))}
                </ul>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-6">Le détail des conditions d'évacuation est rappelé et accepté à l'étape de commande.</p>
          </div>

          <div className="mt-10 text-center">
            <Card className="inline-block p-6 bg-green-50 border-green-200">
              <p className="text-gray-700 mb-3">
                <span className="font-semibold">Vous préférez échanger directement ?</span>
              </p>
              <p className="text-gray-600">
                Appelez-nous au{' '}
                <a href="tel:0745558835" className="text-green-600 hover:text-green-700 font-bold text-lg">
                  07 45 55 88 35
                </a>
              </p>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;